import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  CheckCircle,
  Loader2,
  AlertCircle,
  User,
  Mail,
  Phone,
  Calendar,
  Clock,
  Users,
  FileText,
  ClipboardList,
  MessageSquareWarning,
  Lightbulb,
} from "lucide-react";
import endpoints from "../../config/endpoints.json";
import { LABSYSO_FEEDBACK } from "../../config/labsysoFeedback";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import PrivacyConsentCheckbox from "../PrivacyConsentCheckbox";

const GAS_URL = endpoints.labsysoFeedbackForm?.gasUrl || "";

const RELACION_OPTIONS = [
  { value: "Cliente/Paciente", label: "Cliente / Paciente" },
  { value: "Colaborador", label: "Colaborador" },
  { value: "Otro", label: "Otro" },
];

const AREA_OPTIONS = [
  { value: "Toma de muestras", label: "Toma de muestras" },
  { value: "Entrega de resultados", label: "Entrega de resultados" },
  { value: "Facturación", label: "Facturación" },
  { value: "Atención al cliente", label: "Atención al cliente" },
  { value: "Otro", label: "Otro" },
];

const inputClass =
  "w-full rounded-2xl border border-[#221d52]/15 bg-white px-4 py-3.5 text-sm text-[#221d52] placeholder:text-[#221d52]/35 focus:outline-none focus:ring-2 focus:ring-[#f5b419]/60 focus:border-[#221d52]/25 transition-shadow font-body";

const labelClass =
  "flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#221d52]/75 mb-2 font-display";

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 mb-5 mt-8 first:mt-0 pb-3 border-b border-[#221d52]/10">
    <span className="w-9 h-9 rounded-xl bg-[#221d52]/8 text-[#221d52] flex items-center justify-center">
      <Icon size={17} />
    </span>
    <h3 className="text-[#221d52] font-display font-black uppercase tracking-[0.18em] text-[11px]">
      {children}
    </h3>
  </div>
);

const TipoSolicitudToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2 p-1.5 bg-[#221d52]/8 rounded-2xl mb-6">
    <button
      type="button"
      onClick={() => onChange("queja")}
      className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
        value === "queja"
          ? "bg-[#221d52] text-white shadow-md"
          : "text-[#221d52]/70 hover:text-[#221d52] hover:bg-white/60"
      }`}
    >
      <MessageSquareWarning size={16} />
      Queja / Reclamo
    </button>
    <button
      type="button"
      onClick={() => onChange("sugerencia")}
      className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
        value === "sugerencia"
          ? "bg-[#f5b419] text-[#221d52] shadow-md"
          : "text-[#221d52]/70 hover:text-[#221d52] hover:bg-white/60"
      }`}
    >
      <Lightbulb size={16} />
      Sugerencias
    </button>
  </div>
);

const RadioCard = ({ name, value, checked, label, onChange }) => (
  <label
    className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${
      checked
        ? "border-[#221d52] bg-[#221d52]/5 shadow-sm ring-1 ring-[#f5b419]/40"
        : "border-[#221d52]/12 bg-white hover:border-[#221d52]/25"
    }`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="accent-[#221d52] w-4 h-4"
    />
    <span className="text-sm font-semibold text-[#221d52]">{label}</span>
  </label>
);

const initialForm = {
  nombreCompleto: "",
  email: "",
  telefono: "",
  relacion: "",
  relacionOtro: "",
  fechaIncidente: "",
  horaIncidente: "",
  personasAfectadas: "",
  areaServicio: "",
  areaServicioOtro: "",
  descripcion: "",
  quejaAnterior: "",
  fechaQuejaAnterior: "",
  gestionAnterior: "",
  website_url: "",
};

export default function LabsysoFeedbackForm() {
  const [formData, setFormData] = useState(initialForm);
  const [tipoSolicitud, setTipoSolicitud] = useState("queja");
  const [status, setStatus] = useState("idle");
  const [confirmation, setConfirmation] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const isQueja = tipoSolicitud === "queja";

  const handleTipoSolicitudChange = (tipo) => {
    setTipoSolicitud(tipo);
    if (tipo === "sugerencia") {
      setFormData((prev) => ({
        ...prev,
        fechaIncidente: "",
        horaIncidente: "",
        personasAfectadas: "",
        areaServicio: "",
        areaServicioOtro: "",
        quejaAnterior: "",
        fechaQuejaAnterior: "",
        gestionAnterior: "",
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!privacyAccepted) {
      setStatus("validation");
      return;
    }

    if (
      !formData.nombreCompleto.trim() ||
      !formData.email.includes("@") ||
      !formData.telefono.trim() ||
      !formData.relacion ||
      !formData.descripcion.trim()
    ) {
      setStatus("validation");
      return;
    }

    if (formData.relacion === "Otro" && !formData.relacionOtro.trim()) {
      setStatus("validation");
      return;
    }

    if (isQueja) {
      if (
        !formData.fechaIncidente ||
        !formData.horaIncidente ||
        !formData.areaServicio ||
        !formData.quejaAnterior
      ) {
        setStatus("validation");
        return;
      }

      if (formData.areaServicio === "Otro" && !formData.areaServicioOtro.trim()) {
        setStatus("validation");
        return;
      }

      if (formData.quejaAnterior === "Sí") {
        if (!formData.fechaQuejaAnterior || !formData.gestionAnterior.trim()) {
          setStatus("validation");
          return;
        }
      }
    }

    setStatus("loading");

    const payload = {
      ...formData,
      tipoSolicitud,
      tipoFormulario: isQueja ? "LABSYSO_QUEJAS_FPGC_6_1" : "LABSYSO_SUGERENCIAS_FPGC_6_1",
      formCode: LABSYSO_FEEDBACK.formCode,
      origen: "WEB_LABSYSO",
      privacyConsent: true,
      privacyConsentAt: new Date().toISOString(),
    };

    try {
      let solicitudId = "";
      let receivedAt = "";

      try {
        const submitQueja = httpsCallable(functions, "submitLabsysoQueja");
        const res = await submitQueja({
          ...payload,
          forwardGasUrl: GAS_URL,
        });
        const data = res.data || {};
        if (data.ignored) {
          setStatus("success");
          return;
        }
        if (!data.ok && data.result !== "success") {
          throw new Error(data.message || "Respuesta inválida del servidor");
        }
        solicitudId = data.solicitudId || data.folio || data.id || "";
        receivedAt = data.receivedAt || new Date().toISOString();
      } catch (cfErr) {
        console.warn("[LABSYSO Feedback] CF falló, fallback GAS:", cfErr);
        if (!GAS_URL) throw cfErr;
        const response = await fetch(GAS_URL, {
          method: "POST",
          cache: "no-cache",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (data.result !== "success" || !data.solicitudId) {
          throw new Error(data.message || "Respuesta inválida del servidor");
        }
        solicitudId = data.solicitudId;
        receivedAt = data.receivedAt;
      }

      setConfirmation({ solicitudId, receivedAt });
      setStatus("success");
      setFormData(initialForm);
      setPrivacyAccepted(false);
      setTipoSolicitud("queja");
    } catch (error) {
      console.error("[LABSYSO Feedback] Error en envío:", error);
      setStatus("error");
    }
  };

  if (status === "success" && confirmation) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12 px-4"
      >
        <div className="w-20 h-20 bg-[#f5b419]/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-[#221d52]" size={40} />
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-black text-[#221d52] mb-4">
          Solicitud registrada
        </h3>
        <p className="text-gray-600 max-w-lg mx-auto leading-relaxed mb-6">
          Hemos recibido su solicitud. El equipo de calidad LABSYSO dará seguimiento según el
          procedimiento <strong>{LABSYSO_FEEDBACK.formCode}</strong>.
        </p>

        <div className="max-w-md mx-auto space-y-3 text-left">
          <div className="rounded-2xl bg-[#221d52] text-white px-5 py-4 shadow-lg">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#f5b419] mb-1">
              Código de esta solicitud
            </p>
            <p className="font-display font-black text-lg tracking-wide">{confirmation.solicitudId}</p>
          </div>
          <div className="rounded-2xl bg-[#221d52]/5 border border-[#221d52]/10 px-5 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#221d52]/60 mb-1">
              Fecha y hora de recibida
            </p>
            <p className="text-sm font-semibold text-[#221d52]">{confirmation.receivedAt}</p>
          </div>
        </div>

        <p className="text-xs text-gray-500 max-w-md mx-auto mt-5 leading-relaxed">
          Guarde este código para dar seguimiento a su solicitud con el equipo de calidad LABSYSO.
        </p>

        <button
          type="button"
          onClick={() => {
            setConfirmation(null);
            setStatus("idle");
            setTipoSolicitud("queja");
          }}
          className="mt-8 bg-[#221d52] text-white px-8 py-3.5 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-[#1a1640] transition-colors"
        >
          Enviar otra solicitud
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="text-left space-y-1">
      <div className="rounded-2xl bg-[#221d52]/5 border border-[#221d52]/10 p-4 mb-6">
        <p className="text-sm text-[#221d52]/80 leading-relaxed">
          {LABSYSO_FEEDBACK.subtitle} Los campos con{" "}
          <span className="text-[#aa1e24] font-bold">*</span> son obligatorios.
        </p>
      </div>

      <AnimatePresence>
        {(status === "validation" || status === "error" || status === "config") && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-[#aa1e24]/10 border border-[#aa1e24]/20 text-[#aa1e24]"
          >
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <p className="text-sm font-medium">
              {status === "config"
                ? "El formulario aún no está vinculado al servidor. Configure la URL de Apps Script en endpoints.json (labsysoFeedbackForm.gasUrl)."
                : status === "validation"
                  ? "Complete todos los campos obligatorios antes de enviar."
                  : "No se pudo enviar en este momento. Intente nuevamente o use los canales alternativos del flujograma."}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <SectionTitle icon={User}>Datos del solicitante</SectionTitle>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className={labelClass} htmlFor="nombreCompleto">
            <User size={14} /> Nombre completo <span className="text-[#aa1e24]">*</span>
          </label>
          <input
            id="nombreCompleto"
            name="nombreCompleto"
            value={formData.nombreCompleto}
            onChange={handleChange}
            required
            className={inputClass}
            placeholder="Nombre y apellidos"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="email">
            <Mail size={14} /> Correo electrónico <span className="text-[#aa1e24]">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={inputClass}
            placeholder="correo@ejemplo.com"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="telefono">
            <Phone size={14} /> Teléfono <span className="text-[#aa1e24]">*</span>
          </label>
          <input
            id="telefono"
            name="telefono"
            type="tel"
            value={formData.telefono}
            onChange={handleChange}
            required
            className={inputClass}
            placeholder="+503 0000 0000"
          />
        </div>
      </div>

      <div className="mt-4">
        <p className={labelClass}>
          <ClipboardList size={14} /> Relación con el laboratorio{" "}
          <span className="text-[#aa1e24]">*</span>
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {RELACION_OPTIONS.map((opt) => (
            <RadioCard
              key={opt.value}
              name="relacion"
              value={opt.value}
              label={opt.label}
              checked={formData.relacion === opt.value}
              onChange={handleChange}
            />
          ))}
        </div>
        {formData.relacion === "Otro" && (
          <input
            name="relacionOtro"
            value={formData.relacionOtro}
            onChange={handleChange}
            className={`${inputClass} mt-3`}
            placeholder="Especifique su relación"
            required
          />
        )}
      </div>

      <TipoSolicitudToggle value={tipoSolicitud} onChange={handleTipoSolicitudChange} />

      <SectionTitle icon={FileText}>
        {isQueja ? "Detalles de la queja o reclamo" : "Detalle de su sugerencia"}
      </SectionTitle>

      {isQueja && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="fechaIncidente">
                <Calendar size={14} /> Fecha del incidente <span className="text-[#aa1e24]">*</span>
              </label>
              <input
                id="fechaIncidente"
                name="fechaIncidente"
                type="date"
                value={formData.fechaIncidente}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="horaIncidente">
                <Clock size={14} /> Hora del incidente <span className="text-[#aa1e24]">*</span>
              </label>
              <input
                id="horaIncidente"
                name="horaIncidente"
                type="time"
                value={formData.horaIncidente}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className={labelClass} htmlFor="personasAfectadas">
              <Users size={14} /> Nombre(s) de la(s) persona(s) que tuvo inconveniente
              <span className="text-[#221d52]/45 normal-case tracking-normal font-medium ml-1">
                (opcional)
              </span>
            </label>
            <input
              id="personasAfectadas"
              name="personasAfectadas"
              value={formData.personasAfectadas}
              onChange={handleChange}
              className={inputClass}
              placeholder="Ej.: Juan Pérez, María López"
            />
          </div>

          <div className="mt-4">
            <p className={labelClass}>
              Área o servicio involucrado <span className="text-[#aa1e24]">*</span>
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {AREA_OPTIONS.map((opt) => (
                <RadioCard
                  key={opt.value}
                  name="areaServicio"
                  value={opt.value}
                  label={opt.label}
                  checked={formData.areaServicio === opt.value}
                  onChange={handleChange}
                />
              ))}
            </div>
            {formData.areaServicio === "Otro" && (
              <input
                name="areaServicioOtro"
                value={formData.areaServicioOtro}
                onChange={handleChange}
                className={`${inputClass} mt-3`}
                placeholder="Especifique el área o servicio"
                required
              />
            )}
          </div>
        </>
      )}

      <div className="mt-4">
        <label className={labelClass} htmlFor="descripcion">
          <FileText size={14} />
          {isQueja
            ? "Descripción detallada del problema"
            : "Descripción detallada de la sugerencia"}{" "}
          <span className="text-[#aa1e24]">*</span>
        </label>
        <textarea
          id="descripcion"
          name="descripcion"
          value={formData.descripcion}
          onChange={handleChange}
          required
          rows={5}
          className={`${inputClass} resize-y min-h-[120px]`}
          placeholder={
            isQueja
              ? "Describa con el mayor detalle posible lo ocurrido…"
              : "Comparta su sugerencia para mejorar nuestros servicios…"
          }
        />
      </div>

      {isQueja && (
        <>
          <div className="mt-4">
            <p className={labelClass}>
              ¿Ha presentado esta queja o reclamo anteriormente?{" "}
              <span className="text-[#aa1e24]">*</span>
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {["Sí", "No"].map((opt) => (
                <RadioCard
                  key={opt}
                  name="quejaAnterior"
                  value={opt}
                  label={opt}
                  checked={formData.quejaAnterior === opt}
                  onChange={handleChange}
                />
              ))}
            </div>
          </div>

          {formData.quejaAnterior === "Sí" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="grid md:grid-cols-2 gap-4 mt-4 overflow-hidden"
            >
              <div>
                <label className={labelClass} htmlFor="fechaQuejaAnterior">
                  Fecha en que presentó la queja anterior <span className="text-[#aa1e24]">*</span>
                </label>
                <input
                  id="fechaQuejaAnterior"
                  name="fechaQuejaAnterior"
                  type="date"
                  value={formData.fechaQuejaAnterior}
                  onChange={handleChange}
                  required
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass} htmlFor="gestionAnterior">
                  ¿Cómo fue gestionada? <span className="text-[#aa1e24]">*</span>
                </label>
                <textarea
                  id="gestionAnterior"
                  name="gestionAnterior"
                  value={formData.gestionAnterior}
                  onChange={handleChange}
                  required
                  rows={3}
                  className={`${inputClass} resize-y`}
                  placeholder="Indique cómo se resolvió o dio seguimiento anteriormente"
                />
              </div>
            </motion.div>
          )}
        </>
      )}

      {/* Honeypot */}
      <input
        type="text"
        name="website_url"
        value={formData.website_url}
        onChange={handleChange}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <PrivacyConsentCheckbox
        id="labsyso-privacy"
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
        tone="labsyso"
        className="mt-6"
      />

      <button
        type="submit"
        disabled={status === "loading" || !privacyAccepted}
        className="w-full mt-8 flex items-center justify-center gap-3 bg-[#221d52] hover:bg-[#1a1640] text-white py-4 rounded-2xl font-display font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-[#221d52]/20 transition-all active:scale-[0.99] disabled:opacity-60"
      >
        {status === "loading" ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Enviando…
          </>
        ) : (
          <>
            <Send size={18} />
            Enviar al buzón LABSYSO
          </>
        )}
      </button>

      <p className="text-center text-[10px] text-gray-500 mt-4">
        Registro vinculado a Google Sheets · {LABSYSO_FEEDBACK.formCode}
      </p>
    </form>
  );
}
